import React, { useState, useEffect } from 'react';
import { useT } from '../i18n/LanguageContext';

/**
 * Confirm removing a record. Shared by Orders, IVR and Shipments so all three behave identically.
 *
 * IT SAYS THE DELETE IS REVERSIBLE, BECAUSE IT IS. The server hides the record rather than
 * destroying it - the database has no backups and these rows hold PHI and the history behind real
 * invoices. Telling someone "this cannot be undone" when it can teaches them to distrust the next
 * warning, which is the one that matters.
 *
 * THE SECOND STEP IS WHERE THE REAL RISK LIVES. The server refuses to delete an order that has a
 * QuickBooks invoice. App-authored guidance explains the consequence, and a separate
 * "Delete anyway" appears - because deleting the order does NOT remove the invoice. It stays on a
 * live ledger, billed to a practice, with nothing left in this app saying what it was for. That is
 * the irreversible part, and it deserves its own deliberate click rather than being buried in the
 * first confirmation.
 */
export default function DeleteRecordModal({ open, label, allowForce = false, onCancel, onDelete, onDone }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    if (open) {
      setError('');
      setRefused(false);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const run = async (force) => {
    setBusy(true);
    setError('');
    try {
      await onDelete(force);
      onDone();
    } catch (err) {
      const canOverride = allowForce && err.response?.status === 400;
      setError(canOverride
        ? 'This record could not be deleted. If it has a linked invoice, deleting anyway will leave that invoice in place.'
        : 'Delete failed');
      // A 400 is the server's considered refusal, not a fault - it is the only case where offering
      // to override makes sense. A 500 or a dropped connection must not grow an "anyway" button.
      setRefused(canOverride);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[14px] shadow-xl w-full max-w-[440px] max-w-[calc(100vw-2rem)] p-6">
        <h2 className="text-base font-semibold text-[#0f172a] mb-2">{t('Delete {label}?', { label: t(label) })}</h2>
        <p className="text-xs text-[#64748b] mb-4">
          {t('It will be removed from lists, reports and exports. Nothing is destroyed — an admin can restore it.')}
        </p>

        {!!error && (
          <div className="mb-4 rounded-[10px] border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5">
            <p className="text-xs text-[#b91c1c] leading-relaxed">{t(error)}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-[38px] px-4 rounded-[10px] border border-[#e2e8f0] text-xs font-semibold text-[#0f172a] hover:bg-gray-50 disabled:opacity-50"
          >
            {t('Cancel')}
          </button>
          {refused ? (
            <button
              type="button"
              onClick={() => run(true)}
              disabled={busy}
              className="h-[38px] px-4 rounded-[10px] bg-[#b91c1c] text-white text-xs font-semibold hover:bg-[#991b1b] disabled:opacity-50"
            >
              {busy ? t('Deleting...') : t('Delete anyway')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => run(false)}
              disabled={busy}
              className="h-[38px] px-4 rounded-[10px] bg-[#f23e41] text-white text-xs font-semibold hover:bg-[#d93235] disabled:opacity-50"
            >
              {busy ? t('Deleting...') : t('Delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
